<script lang="ts">
    // RAIO-X v2
    //
    // Mesmos números do raio-x anterior (dashboard-xray.svelte, que continua no projeto) — o que muda é a
    // leitura: cada indicador vira um cartão com o número do mês, a variação contra o mês anterior e o
    // dia a dia dentro do mês. Os quatro meses continuam lado a lado; só o mês de referência tem cor,
    // os anteriores ficam em cinza, para servirem de comparação sem disputar a atenção.
    //
    // As séries diárias NÃO vêm prontas do servidor: são montadas aqui a partir do `details` que a
    // resposta já carrega (os registros crus, com data). Ver "SÉRIES DIÁRIAS" mais abaixo.
    import { onMount } from 'svelte/internal'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZsheetSection from '../_common/z/sheet/zsheet-section.svelte'
    import dayjs from 'dayjs'
    import Fa from 'svelte-fa'
    import { faArrowDown, faArrowUp, faEquals, faPlus, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
    import Popover from '../_common/popover.svelte'
    import DashboardXrayDetails from './dashboard-xray-details.svelte'
    import Zact from '../_common/z/zact.svelte'

    let show = false

    onMount(async () => {
        await load(date)
    })

    type Tone = 'green' | 'yellow' | 'red' | 'grey'
    type Evolution = { text: string; tone: Tone; direction: 'up' | 'down' | null }
    type DetailsType = 'contacted' | 'connected' | 'added' | 'show' | 'rebuy' | 'converted' | 'direct' | 'all' | 'old'

    // cada cartão do raio-x: de onde vem o número, como mostrar, como montar o dia a dia e qual
    // detalhamento a lupa abre
    type Metric = {
        key: string
        title: string
        help?: string
        value: (result: any) => number
        format?: 'int' | 'percent' | 'money'
        details?: (result: any) => any[]
        detailsType?: DetailsType
        /** valores por dia do mês. Conta = coluna; acumulado = linha (ver "SÉRIES DIÁRIAS") */
        serie: (result: any, dias: number) => number[]
        grafico: 'coluna' | 'linha'
    }

    // os grupos seguem o funil: captação ↓ comparecimento e conversão + outras compras = resultado
    const groups: { title: string; icon: any; metrics: Metric[] }[] = [
        {
            title: 'Captação',
            icon: faArrowDown,
            metrics: [
                {
                    key: 'contacted',
                    title: 'Tentativas de contato',
                    help: 'Contatos sem histórico de compras que tiveram ao menos um contato no período.',
                    value: (r) => r.com.contacted,
                    details: (r) => r.com.details,
                    detailsType: 'contacted',
                    serie: (r, dias) => seriePessoas(r.com.details?.persons, 'externalDate', r.month, dias),
                    grafico: 'coluna',
                },
                {
                    key: 'connected',
                    title: 'Contatos com resposta',
                    value: (r) => r.com.connected,
                    details: (r) => r.com.details,
                    detailsType: 'connected',
                    serie: (r, dias) => seriePessoas(r.com.details?.persons, 'externalDate', r.month, dias, ids(r.com.details?.connected)),
                    grafico: 'coluna',
                },
                {
                    key: 'scheduled',
                    title: 'Agendamento incluído',
                    help: 'Agendamentos de serviços do tipo avaliação / reunião / orçamento, pela data de inclusão.',
                    value: (r) => r.sched.scheduled,
                    details: (r) => r.sched.details,
                    detailsType: 'added',
                    serie: (r, dias) => seriePessoas(r.sched.details?.persons, 'createdAt', r.month, dias),
                    grafico: 'coluna',
                },
            ],
        },
        {
            title: 'Comparecimento e conversão',
            icon: faArrowDown,
            metrics: [
                {
                    key: 'showFromMine',
                    title: 'Agendamento compareceu',
                    value: (r) => r.sched.showFromMine,
                    details: (r) => r.sched.details,
                    detailsType: 'show',
                    serie: (r, dias) => serieCompareceu(r, dias),
                    grafico: 'coluna',
                },
                {
                    key: 'showFromMinePercentual',
                    title: 'Taxa de presença',
                    value: (r) => r.sched.showFromMinePercentual,
                    format: 'percent',
                    serie: (r, dias) => razaoAcumulada(serieCompareceu(r, dias), seriePessoas(r.sched.details?.persons, 'createdAt', r.month, dias)),
                    grafico: 'linha',
                },
                {
                    key: 'convertedByMe',
                    title: 'Agendados que compraram',
                    value: (r) => r.conv.convertedByMe,
                    details: (r) => r.conv.details,
                    detailsType: 'converted',
                    serie: (r, dias) => serieConvertidos(r, dias),
                    grafico: 'coluna',
                },
                {
                    key: 'convertedByMePercentual',
                    title: 'Taxa de conversão',
                    value: (r) => r.conv.convertedByMePercentual,
                    format: 'percent',
                    serie: (r, dias) => razaoAcumulada(serieConvertidos(r, dias), serieCompareceu(r, dias)),
                    grafico: 'linha',
                },
            ],
        },
        {
            title: 'Outras compras',
            icon: faPlus,
            metrics: [
                {
                    key: 'totalOldCount',
                    title: 'Comprou agend. antigos',
                    value: (r) => r.conv.totalOldCount,
                    details: (r) => r.conv.details,
                    detailsType: 'old',
                    serie: (r, dias) => seriePessoas(r.conv.details?.persons, 'createdAt', r.month, dias, ids(r.conv.details?.old)),
                    grafico: 'coluna',
                },
                {
                    key: 'totalDirectCount',
                    title: 'Comprou sem agendar',
                    value: (r) => r.conv.totalDirectCount,
                    details: (r) => r.conv.details,
                    detailsType: 'direct',
                    serie: (r, dias) => seriePessoas(r.conv.details?.persons, 'createdAt', r.month, dias, ids(r.conv.details?.direct)),
                    grafico: 'coluna',
                },
                {
                    key: 'totalCountRebuy',
                    title: 'Recompras',
                    value: (r) => r.conv.totalCountRebuy,
                    details: (r) => r.conv.details,
                    detailsType: 'rebuy',
                    serie: (r, dias) => seriePessoas(r.conv.details?.persons, 'createdAt', r.month, dias, ids(r.conv.details?.rebuy)),
                    grafico: 'coluna',
                },
            ],
        },
        {
            title: 'Resultado',
            icon: faEquals,
            metrics: [
                {
                    key: 'totalCount',
                    title: 'Total de compradores',
                    value: (r) => r.conv.totalCount,
                    details: (r) => r.conv.details,
                    detailsType: 'all',
                    serie: (r, dias) => serieCompradores(r, dias),
                    grafico: 'coluna',
                },
                {
                    key: 'ticket',
                    title: 'Ticket médio',
                    value: (r) => r.conv.ticket,
                    format: 'money',
                    serie: (r, dias) => razaoAcumulada(serieVendido(r, dias), serieCompradores(r, dias), 1),
                    grafico: 'linha',
                },
                {
                    key: 'totalValue',
                    title: 'Total vendido',
                    value: (r) => r.conv.totalValue,
                    format: 'money',
                    serie: (r, dias) => serieVendido(r, dias),
                    grafico: 'coluna',
                },
            ],
        },
    ]

    // ###################################################################### SÉRIES DIÁRIAS
    //
    // O servidor devolve o número do mês inteiro e, junto, os registros crus em `details` — eventos de
    // comunicação (externalDate), agendamentos (createdAt) e vendas (createdAt). O dia a dia é montado
    // daqui, sem chamada nova.
    //
    // Duas regras que fazem as colunas fecharem com o número grande do cartão:
    //
    // 1. TODA contagem do raio-x é de PESSOAS DISTINTAS no mês, não de registros. Então cada pessoa entra
    //    uma única vez, no dia do seu primeiro registro que conta — se ela foi contactada em três dias,
    //    aparece no primeiro. Contar "pessoas do dia" faria a soma das colunas passar do total do mês.
    //
    // 2. `conv.details.persons[].all` também carrega vendas de FORA do período (o cálculo da conversão
    //    olha para a frente), por isso todo registro é filtrado pelo mês do próprio cartão.

    const ids = (lista: string[]) => (Array.isArray(lista) ? new Set(lista) : null)

    const doMes = (data: any, mes: string) => {
        const d = dayjs(data)
        return d.isValid() && d.format('MM/YYYY') === mes
    }

    /** uma pessoa por dia do seu primeiro registro no mês; `somente` restringe a um subconjunto de pessoas */
    function seriePessoas(persons: any[], campo: string, mes: string, dias: number, somente: Set<string> = null, filtro: (reg: any) => boolean = null) {
        const serie = new Array(dias).fill(0)
        for (const pessoa of persons || []) {
            if (somente && !somente.has(pessoa.personId)) continue
            let primeiro = 0
            for (const registro of pessoa.all || []) {
                if (filtro && !filtro(registro)) continue
                if (!doMes(registro[campo], mes)) continue
                const dia = dayjs(registro[campo]).date()
                if (!primeiro || dia < primeiro) primeiro = dia
            }
            if (primeiro >= 1 && primeiro <= dias) serie[primeiro - 1] += 1
        }
        return serie
    }

    /** soma de valores por dia (o único indicador somável: o total vendido) */
    function serieSoma(persons: any[], campo: string, mes: string, dias: number, valorDe: (reg: any) => number) {
        const serie = new Array(dias).fill(0)
        for (const pessoa of persons || []) {
            for (const registro of pessoa.all || []) {
                if (!doMes(registro[campo], mes)) continue
                const dia = dayjs(registro[campo]).date()
                if (dia >= 1 && dia <= dias) serie[dia - 1] += valorDe(registro) || 0
            }
        }
        return serie
    }

    /**
     * Razão/média ACUMULADA: cada ponto é o valor do mês até aquele dia, e o fim da linha é exatamente o
     * número do cartão. O valor isolado de um dia não serve aqui — com dois agendamentos no dia, a taxa de
     * presença só pode dar 0%, 50% ou 100%, e o gráfico viraria ruído.
     */
    function razaoAcumulada(numerador: number[], denominador: number[], fator = 100) {
        const serie = new Array(numerador.length).fill(0)
        let num = 0
        let den = 0
        for (let i = 0; i < numerador.length; i++) {
            num += numerador[i] || 0
            den += denominador[i] || 0
            serie[i] = den > 0 ? (num / den) * fator : 0
        }
        return serie
    }

    // os três abaixo são usados por mais de um cartão (um como contagem, outro como denominador da razão)
    const serieCompareceu = (r: any, dias: number) =>
        seriePessoas(r.sched.details?.persons, 'createdAt', r.month, dias, ids(r.sched.details?.show), (ap) => ap.status === 'agendamento_compareceu')

    // "Agendados que compraram" conta a pessoa no dia do AGENDAMENTO, não no da compra: o cartão mede a
    // safra de quem foi agendado neste mês, e a compra dela pode cair no mês seguinte (janela de conversão).
    // Pelo dia da compra, parte da safra sumiria do gráfico e as colunas não fechariam com o número.
    const serieConvertidos = (r: any, dias: number) => seriePessoas(r.sched.details?.persons, 'createdAt', r.month, dias, ids(r.conv.details?.converted))

    const serieCompradores = (r: any, dias: number) => seriePessoas(r.conv.details?.persons, 'createdAt', r.month, dias, ids(r.conv.details?.all))

    const serieVendido = (r: any, dias: number) => serieSoma(r.conv.details?.persons, 'createdAt', r.month, dias, (inv) => inv.total)

    // o que cada cor quer dizer — é a mesma regra do getColor, escrita para quem lê a tela
    const legend: { tone: Tone; text: string }[] = [
        { tone: 'green', text: 'Subiu mais de 10%' },
        { tone: 'grey', text: 'Estável' },
        { tone: 'yellow', text: 'Caiu até 10%' },
        { tone: 'red', text: 'Caiu mais de 10%' },
    ]

    const monthNames = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']

    let results: any[] = []
    let dataCount = 0
    let referenceMonth: Date = new Date()
    let date = dayjs(referenceMonth).format('YYYY-MM')
    let titleLabel = ''
    let spinnerInfo = ''
    let conversionWindow = 30
    const conversionWindowOptions = Array.from({ length: 90 }, (_, index) => ({ value: index + 1, label: index + 1 + (index === 0 ? ' dia' : ' dias') }))

    // o Zinput de mês devolve Date; o relatório trabalha com 'YYYY-MM'. Campo limpo mantém o mês atual.
    $: if (referenceMonth instanceof Date && !isNaN(referenceMonth.getTime())) {
        const picked = dayjs(referenceMonth).format('YYYY-MM')
        if (picked !== date) {
            date = picked
            load(date)
        }
    }

    let loadedWindow = conversionWindow
    $: if (conversionWindow && conversionWindow !== loadedWindow) {
        loadedWindow = conversionWindow
        load(date)
    }

    // limite de chamadas simultâneas ao relatório — cada mês vira 1 requisição independente ao zandu-api
    // (não há problema de estado compartilhado entre elas, ver dashboard-xray-calculator.ts), mas ainda assim
    // evitamos disparar todas de uma vez pra não sobrecarregar o servidor/pos-api.
    const MAX_CONCURRENT_REQUESTS = 2

    // roda `tasks` respeitando no máximo `limit` execuções simultâneas, preservando a ordem original no
    // array de resultado (independente da ordem em que cada tarefa termina)
    async function runWithConcurrency<T>(tasks: (() => Promise<T>)[], limit: number): Promise<T[]> {
        const results: T[] = new Array(tasks.length)
        let next = 0
        async function worker() {
            while (next < tasks.length) {
                const index = next++
                results[index] = await tasks[index]()
            }
        }
        await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker))
        return results
    }

    let cols = 4
    let cutoffDay: number | undefined = undefined

    async function load(dateRef: string) {
        spinnerInfo = 'Carregando...'
        show = true

        const isMobile = screen.width <= 900
        cols = isMobile ? 2 : 4

        titleLabel = ''
        // quando o mês mais recente é o mês corrente, corta TODOS os meses da série no dia de hoje (não só o
        // corrente) — pra comparação justa entre meses (dia 1 a X de cada um, em vez de meses completos
        // contra um mês em andamento). Como cada mês é uma chamada independente ao zandu-api, esse corte
        // precisa ir explícito em todas elas — ver DashboardXrayParams.cutoffDay.
        const today = dayjs()
        const reference = dayjs(dateRef + '-15')
        cutoffDay = undefined
        const isCurrentCalendarMonth = reference.year() === today.year() && reference.month() === today.month()
        if (isCurrentCalendarMonth && today.date() < reference.daysInMonth()) {
            titleLabel = 'Até o dia ' + today.date()
            cutoffDay = today.date()
        }

        results = []
        dataCount = 0

        try {
            // cálculo roda no zandu-api (30-zandu-api/src/modules/reports/dashboard) — 1 chamada por mês, em
            // paralelo (no máximo MAX_CONCURRENT_REQUESTS por vez).
            const monthRefs = Array.from({ length: cols }, (_, index) =>
                dayjs(dateRef + '-15')
                    .subtract(index, 'month')
                    .format('YYYY-MM'),
            )
            const tasks = monthRefs.map(
                (month) => () =>
                    window.api.reports.catalog.run('/reports/dashboard/xray', {
                        month,
                        months: 1,
                        conversionWindowDays: conversionWindow,
                        cutoffDay,
                    }),
            )
            const responses: any[] = await runWithConcurrency(tasks, MAX_CONCURRENT_REQUESTS)

            dataCount = responses.reduce((sum, response) => sum + response.dataCount, 0)
            results = responses.flatMap((response) => response.results).map((result) => ({ ...result, evolution: {}, series: {} }))
            results.reverse() // cada resposta traz 1 mês só; monthRefs vem do mais recente pro mais antigo
        } catch (error) {
            window.error(error)
            show = false
            return
        }

        const metrics = groups.flatMap((group) => group.metrics)
        for (let index = 0; index < results.length; index++) {
            const result = results[index]
            // quantas colunas o mês tem: o mês inteiro, ou só até o corte quando a série foi cortada
            const diasDoMes = dayjs(result.from).daysInMonth()
            const dias = cutoffDay ? Math.min(cutoffDay, diasDoMes) : diasDoMes
            result.dias = dias
            // o desenho usa o mês inteiro como régua, para o mês parcial começar na esquerda
            result.diasDoMes = diasDoMes
            for (const metric of metrics) {
                // evolução contra o mês anterior (a primeira coluna não tem com quem comparar)
                result.evolution[metric.key] =
                    index > 0 ? getPercentS(metric.value(results[index - 1]), metric.value(result)) : { text: '', tone: 'grey', direction: null }
                // série diária, calculada uma vez por cartão (não a cada desenho)
                try {
                    result.series[metric.key] = metric.serie(result, dias)
                } catch (error) {
                    result.series[metric.key] = new Array(dias).fill(0)
                }
            }
        }

        results = results
        show = false
    }

    // escala compartilhada por indicador: os quatro meses do mesmo cartão usam a mesma altura máxima,
    // senão um mês fraco pareceria igual a um mês forte
    $: maxPorMetrica = (() => {
        const mapa: Record<string, number> = {}
        for (const group of groups) {
            for (const metric of group.metrics) {
                let max = 0
                for (const result of results) {
                    for (const valor of result.series?.[metric.key] || []) if (valor > max) max = valor
                }
                mapa[metric.key] = max
            }
        }
        return mapa
    })()

    function getPercentS(from: number, to: number): Evolution {
        const percent = (100 / from) * to - 100
        if (percent === Infinity) return { text: '∞', tone: 'grey', direction: 'up' }
        if (isNaN(percent)) return { text: '', tone: 'grey', direction: null }
        if (Math.round(percent) === 0) return { text: '', tone: 'grey', direction: null }

        const percentRound = Math.abs(Math.round(percent)) + '%'
        return { text: percentRound, tone: getColor(percent), direction: percent > 0 ? 'up' : 'down' }
    }
    function getColor(percent): Tone {
        if (percent > 10) return 'green'
        if (percent < -10) return 'red'
        if (percent >= -10 && percent < 0) return 'yellow'
        return 'grey'
    }

    function formatValue(metric: Metric, result: any) {
        const value = metric.value(result)
        if (metric.format === 'percent') return value + '%'
        if (metric.format === 'money') return window.api.utils.utils.toReal(value)
        return value
    }

    // o relatório devolve 'MM/YYYY'; na tela fica "setembro 2026"
    function monthTitle(month: string) {
        const [mm, yyyy] = (month || '').split('/')
        const name = monthNames[Number(mm) - 1]
        return name && yyyy ? name + ' ' + yyyy : month
    }

    function evolutionHint(evolution: Evolution) {
        if (!evolution?.text) return 'Sem variação em relação ao mês anterior'
        if (evolution.text === '∞') return 'O mês anterior estava zerado'
        return (evolution.direction === 'up' ? 'Subiu ' : 'Caiu ') + evolution.text + ' em relação ao mês anterior'
    }

    // ---- desenho do gráfico (SVG à mão: são até 48 gráficos numa tela só) ----
    const ALTURA = 40

    // A barra nunca passa de 10px. O gráfico é desenhado numa caixa de 100 unidades esticada até a largura
    // do cartão (preserveAspectRatio="none"), então um mês parcial — com 8 ou 10 colunas — engordava a barra
    // junto. O limite vem em pixels e é convertido para as unidades do desenho com a largura medida do
    // cartão; sem medida ainda, usa um teto conservador.
    const LARGURA_MAXIMA_BARRA_PX = 10
    // espaçamento da grade e recuo do cartão — os mesmos números do CSS lá embaixo (.xrv-grade / .xrv-cartao);
    // servem só para descontar o que não é gráfico da largura medida
    const GRADE_GAP_PX = 20
    const GRADE_GAP_PX_MOBILE = 10
    const CARTAO_RECUO_PX = 14
    const CARTAO_RECUO_PX_MOBILE = 10

    let larguraGrade = 0
    $: larguraGrafico = (() => {
        if (!larguraGrade || !results.length) return 0
        const mobile = larguraGrade <= 900
        const gap = mobile ? GRADE_GAP_PX_MOBILE : GRADE_GAP_PX
        const recuo = mobile ? CARTAO_RECUO_PX_MOBILE : CARTAO_RECUO_PX
        const coluna = (larguraGrade - (results.length - 1) * gap) / results.length
        return Math.max(coluna - recuo * 2 - 2, 40) // 2 = as bordas do cartão
    })()
    $: larguraMaximaBarra = larguraGrafico > 0 ? (LARGURA_MAXIMA_BARRA_PX / larguraGrafico) * 100 : 7

    // O passo vem do mês INTEIRO, não da quantidade de colunas desenhadas: num mês parcial as barras
    // ficam alinhadas à esquerda, na mesma posição e na mesma largura de um mês fechado, e o vazio à
    // direita mostra o que ainda falta do mês.
    function colunas(serie: number[], max: number, limite: number, diasDoMes: number) {
        const passo = 100 / (diasDoMes || serie.length || 1)
        const largura = Math.min(passo * 0.7, limite)
        return serie.map((valor, i) => ({
            x: i * passo,
            largura,
            altura: max > 0 && valor > 0 ? Math.max((valor / max) * ALTURA, 1) : 0,
        }))
    }

    function pontos(serie: number[], max: number) {
        const total = serie.length || 1
        const passo = total > 1 ? 100 / (total - 1) : 0
        return serie.map((valor, i) => ({ x: i * passo, y: ALTURA - (max > 0 ? (valor / max) * ALTURA : 0) }))
    }

    const linhaDe = (serie: number[], max: number) =>
        pontos(serie, max)
            .map((p) => p.x.toFixed(2) + ',' + p.y.toFixed(2))
            .join(' ')

    const areaDe = (serie: number[], max: number) => '0,' + ALTURA + ' ' + linhaDe(serie, max) + ' 100,' + ALTURA

    let details = null
    let detailsType: DetailsType = null
    function showDetails(_details: any[], _detailsType) {
        detailsType = _detailsType
        details = _details
    }
</script>

<LoadSpinner {show}>{spinnerInfo}</LoadSpinner>
{#if details}
    <Popover title="Detalhamento" width="1250px" height="100px" on:close={() => (details = null)}>
        <DashboardXrayDetails {detailsType} {details}></DashboardXrayDetails>
    </Popover>
{/if}

<div class="base-dash sales-dash xrv-root">
    <!-- os degradês ficam uma vez só e são reaproveitados por todos os cartões. Moram DENTRO da raiz
         porque é lá que estão as variáveis de cor de cada tema. -->
    <svg width="0" height="0" aria-hidden="true" focusable="false" class="xrv-defs">
        <defs>
            <!-- a cor vem do CSS (stop-color é propriedade), para o tema claro poder trocá-la -->
            <linearGradient id="xrv-area-ref" x1="0" y1="0" x2="0" y2="1">
                <stop class="xrv-stop-ref-topo" offset="0%" />
                <stop class="xrv-stop-ref-base" offset="100%" />
            </linearGradient>
            <linearGradient id="xrv-area-old" x1="0" y1="0" x2="0" y2="1">
                <stop class="xrv-stop-antigo-topo" offset="0%" />
                <stop class="xrv-stop-antigo-base" offset="100%" />
            </linearGradient>
        </defs>
    </svg>

    <Zact>
        <div class="xrv-filtro">
            <Zinput type="month" label="Mês de referência" bind:value={referenceMonth} />
        </div>
        <div class="xrv-filtro">
            <Zinput type="select" label="Janela de conversão" options={conversionWindowOptions} bind:value={conversionWindow} />
        </div>
    </Zact>

    <Zframe
        title="Raio-X da empresa"
        class="h-space"
        headline="Diagnóstico do seu funil de vendas com base em {dataCount} dados coletados e analisados no período."
    >
        <div class="xrv-legenda">
            {#each legend as item}
                <span class="xrv-legenda-item"><span class="xrv-ponto xr-tone-{item.tone}" />{item.text}</span>
            {/each}
            <span class="xrv-legenda-nota">A variação é sempre contra o mês anterior. A lupa abre quem está no número.</span>
        </div>

        {#if results.length}
            <!-- a largura sai do próprio contêiner (ver larguraMaximaBarra). NÃO medir com um elemento
                 filho comum: ele vira um item da grade e empurra os cartões para a célula seguinte. -->
            <div class="xrv-grade" style="--xrv-cols:{results.length}" bind:clientWidth={larguraGrade}>
                <!-- cabeçalho: um mês por coluna; o último é o mês de referência -->
                {#each results as result, index}
                    <div class="xrv-mes" class:is-ref={index === results.length - 1}>
                        <div class="xrv-mes-nome">{monthTitle(result.month)}</div>
                        {#if titleLabel}<div class="xrv-mes-corte">{titleLabel.toLowerCase()}</div>{/if}
                    </div>
                {/each}

                {#each groups as group}
                    <!-- o sinal do funil (↓ + =) se repete em cada coluna -->
                    {#each results as _}
                        <div class="xrv-sinal"><Fa icon={group.icon} /></div>
                    {/each}

                    {#each group.metrics as metric}
                        {#each results as result, index}
                            {@const evolution = result.evolution[metric.key]}
                            {@const serie = result.series[metric.key] || []}
                            {@const ehRef = index === results.length - 1}
                            <div class="xrv-cartao" class:is-ref={ehRef}>
                                <div class="xrv-topo">
                                    <span class="xrv-rotulo" title={metric.help || ''}>{metric.title}</span>
                                    {#if metric.details}
                                        <button
                                            type="button"
                                            class="xrv-lupa"
                                            title="Ver quem está neste número"
                                            on:click={() => showDetails(metric.details(result), metric.detailsType)}
                                        >
                                            <Fa icon={faMagnifyingGlass} />
                                        </button>
                                    {/if}
                                </div>

                                <div class="xrv-valor">{formatValue(metric, result)}</div>

                                <div class="xrv-variacao" title={evolutionHint(evolution)}>
                                    {#if evolution.text}
                                        <span class="xrv-selo xr-tone-{evolution.tone}">
                                            {#if evolution.direction === 'up'}<Fa icon={faArrowUp} />{:else if evolution.direction === 'down'}<Fa
                                                    icon={faArrowDown}
                                                />{/if}
                                            {evolution.text}
                                        </span>
                                        <span class="xrv-variacao-nota">vs mês anterior</span>
                                    {:else}
                                        <span class="xrv-variacao-nota">sem variação no mês anterior</span>
                                    {/if}
                                </div>

                                <svg class="xrv-grafico" viewBox="0 0 100 {ALTURA}" preserveAspectRatio="none" aria-hidden="true" focusable="false">
                                    {#if metric.grafico === 'linha'}
                                        <polygon points={areaDe(serie, maxPorMetrica[metric.key])} fill="url(#{ehRef ? 'xrv-area-ref' : 'xrv-area-old'})" />
                                        <polyline
                                            points={linhaDe(serie, maxPorMetrica[metric.key])}
                                            fill="none"
                                            class={ehRef ? 'xrv-traco is-ref' : 'xrv-traco'}
                                            stroke-width="1.5"
                                            stroke-linejoin="round"
                                            vector-effect="non-scaling-stroke"
                                        />
                                    {:else}
                                        {#each colunas(serie, maxPorMetrica[metric.key], larguraMaximaBarra, result.diasDoMes) as coluna}
                                            <rect
                                                x={coluna.x}
                                                y={ALTURA - coluna.altura}
                                                width={coluna.largura}
                                                height={coluna.altura}
                                                class={ehRef ? 'xrv-barra is-ref' : 'xrv-barra'}
                                            />
                                        {/each}
                                    {/if}
                                </svg>
                            </div>
                        {/each}
                    {/each}
                {/each}
            </div>
        {/if}
    </Zframe>

    <ZsheetSection class="h-space" label="Como ler os cartões" collapsible={true}>
        <dl class="xrv-ajuda">
            <dt>Gráfico - colunas</dt>
            <dd>Cada coluna é um dia do mês, do dia 1 ao último. Somadas, dão o número do cartão.</dd>

            <dt>Gráfico - linha</dt>
            <dd>Taxa de presença, taxa de conversão e ticket médio não se somam: cada ponto é o valor do mês até aquele dia.</dd>

            <dt>Cada pessoa conta uma vez</dt>
            <dd>
                Todos os números são de pessoas distintas no mês. No gráfico, a pessoa aparece no dia do primeiro registro dela — mesmo que tenha sido
                contactada, agendada ou tenha comprado várias vezes.
            </dd>

            <dt>Tentativas de contato</dt>
            <dd>Todos os contatos que não possuem histórico de compras e ocorreu ao menos um contato no período atual.</dd>

            <dt>Agendamento incluído</dt>
            <dd>
                Agendamentos para um serviço que esteja configurado como "avaliação / reunião / orçamento" no cadastro de serviços. Os agendamentos entram no
                mês pela data em que foram incluídos no sistema, e não pela data para a qual a pessoa foi marcada.
            </dd>

            <dt>Agendados que compraram</dt>
            <dd>
                Pessoas com agendamento no período atual que também compraram no período atual ou em uma data futura, conforme janela de conversão selecionada.
                No gráfico, a pessoa aparece no dia do agendamento — a compra dela pode cair no mês seguinte.
            </dd>

            <dt>Comprou agendamentos antigos</dt>
            <dd>Pessoas com compras, e que o agendamento ocorreu antes do período atual em até 90 dias. Não inclui 'recompras'.</dd>

            <dt>Comprou sem agendar</dt>
            <dd>Pessoas com compras feitas sem agendamento ou que o agendamento tenha ocorrido há mais de 90 dias.</dd>

            <dt>Recompra</dt>
            <dd>Pessoas que tenham comprado em períodos anteriores em até 18 meses e voltaram a comprar no período atual.</dd>
        </dl>
    </ZsheetSection>
</div>

<style>
    /* Valores base = tema ESCURO (o padrão do app). O tema claro redefine as mesmas variáveis logo abaixo:
       o cinza calibrado para fundo escuro vira quase invisível sobre branco, e a opacidade que apaga os
       meses anteriores precisa ser bem menor ali. */
    .xrv-root {
        --xrv-cor-ref: #4fc28f;
        --xrv-cor-antigo: #9aa0a6;
        --xrv-area-ref-opacidade: 0.45;
        --xrv-area-antigo-opacidade: 0.25;
        --xrv-cartao-bg: #99999910;
        --xrv-cartao-bg-ref: #99999918;
        --xrv-cartao-borda: #99999925;
        --xrv-cartao-borda-ref: #99999940;
        /* o quanto os meses anteriores recuam: o contraste já é dado pela cor do gráfico */
        --xrv-opacidade-antigo: 0.72;
        --xrv-opacidade-mes: 0.6;
        --xrv-lupa-bg: #99999925;
        --xrv-lupa-bg-hover: #99999945;
    }
    :global(.theme_light) .xrv-root {
        --xrv-cor-ref: #2f765a;
        --xrv-cor-antigo: #6b7280;
        --xrv-area-ref-opacidade: 0.3;
        --xrv-area-antigo-opacidade: 0.18;
        --xrv-cartao-bg: #00000008;
        --xrv-cartao-bg-ref: #00000010;
        --xrv-cartao-borda: #0000001f;
        --xrv-cartao-borda-ref: #00000033;
        --xrv-opacidade-antigo: 0.92;
        --xrv-opacidade-mes: 0.8;
        --xrv-lupa-bg: #00000014;
        --xrv-lupa-bg-hover: #00000026;
    }

    /* cor do gráfico: do app no mês de referência, cinza nos anteriores */
    .xrv-barra {
        fill: var(--xrv-cor-antigo);
    }
    .xrv-barra.is-ref {
        fill: var(--xrv-cor-ref);
    }
    .xrv-traco {
        stroke: var(--xrv-cor-antigo);
    }
    .xrv-traco.is-ref {
        stroke: var(--xrv-cor-ref);
    }
    .xrv-stop-ref-topo {
        stop-color: var(--xrv-cor-ref);
        stop-opacity: var(--xrv-area-ref-opacidade);
    }
    .xrv-stop-ref-base {
        stop-color: var(--xrv-cor-ref);
        stop-opacity: 0;
    }
    .xrv-stop-antigo-topo {
        stop-color: var(--xrv-cor-antigo);
        stop-opacity: var(--xrv-area-antigo-opacidade);
    }
    .xrv-stop-antigo-base {
        stop-color: var(--xrv-cor-antigo);
        stop-opacity: 0;
    }

    /* as quatro cores têm significado (ver `legend`): elas vivem no selo da variação. O gráfico usa a cor
       do app no mês de referência e cinza nos anteriores, para não competir com elas. */
    .xr-tone-green {
        --xrv-tone: #157c53;
    }
    .xr-tone-yellow {
        --xrv-tone: #b67503;
    }
    .xr-tone-red {
        --xrv-tone: #dc3545;
    }
    .xr-tone-grey {
        --xrv-tone: #666666;
    }

    .xrv-defs {
        position: absolute;
    }

    .xrv-filtro {
        width: 240px;
        max-width: 100%;
    }

    /* ---------- legenda ---------- */
    .xrv-legenda {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px 18px;
        margin-bottom: 16px;
        font-size: 13px;
    }
    .xrv-legenda-item {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
    .xrv-ponto {
        width: 10px;
        height: 10px;
        border-radius: 3px;
        background: var(--xrv-tone);
    }
    .xrv-legenda-nota {
        opacity: 0.6;
    }

    /* ---------- grade: uma coluna por mês ---------- */
    .xrv-grade {
        /* a medição de largura do Svelte insere um iframe absoluto aqui dentro: ele precisa de um pai
           posicionado, e por ser absoluto não vira item da grade */
        position: relative;
        display: grid;
        grid-template-columns: repeat(var(--xrv-cols), minmax(0, 1fr));
        gap: 12px 20px;
        align-items: stretch;
    }

    .xrv-mes {
        padding: 10px 12px;
        border-radius: 10px;
        background: var(--xrv-cartao-bg-ref);
        text-align: center;
        opacity: var(--xrv-opacidade-mes);
    }
    .xrv-mes.is-ref {
        opacity: 1;
        box-shadow: inset 0 -2px 0 var(--xrv-cor-ref);
    }
    .xrv-mes-nome {
        font-size: 17px;
        font-weight: 600;
        text-transform: capitalize;
    }
    .xrv-mes-corte {
        font-size: 12px;
        opacity: 0.6;
    }

    .xrv-sinal {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 6px 0px 2px 0px;
        font-size: 11px;
        opacity: 0.5;
    }

    /* ---------- cartão ---------- */
    .xrv-cartao {
        display: flex;
        flex-direction: column;
        padding: 14px 14px 0px 14px;
        border-radius: 14px;
        border: 1px solid var(--xrv-cartao-borda);
        background: var(--xrv-cartao-bg);
        overflow: hidden;
        /* o mês de referência é o único colorido; os anteriores ficam em segundo plano */
        opacity: var(--xrv-opacidade-antigo);
    }
    .xrv-cartao.is-ref {
        opacity: 1;
        background: var(--xrv-cartao-bg-ref);
        border-color: var(--xrv-cartao-borda-ref);
    }

    .xrv-topo {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        min-height: 28px;
    }
    .xrv-rotulo {
        flex: 1;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        opacity: 0.7;
        line-height: 1.3;
    }
    .xrv-rotulo[title]:not([title='']) {
        cursor: help;
    }
    .xrv-lupa {
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border: 0px;
        border-radius: 8px;
        background: var(--xrv-lupa-bg);
        color: inherit;
        font-size: 12px;
        cursor: pointer;
        opacity: 0.8;
        transition:
            background-color 0.14s ease,
            opacity 0.14s ease;
    }
    .xrv-lupa:hover {
        background: var(--xrv-lupa-bg-hover);
        opacity: 1;
    }

    .xrv-valor {
        margin-top: 2px;
        font-size: 30px;
        font-weight: 600;
        line-height: 1.15;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }

    .xrv-variacao {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 4px;
        font-size: 12px;
        min-height: 20px;
    }
    .xrv-selo {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-weight: 600;
        color: var(--xrv-tone);
    }
    .xrv-selo :global(svg) {
        font-size: 10px;
    }
    .xrv-variacao-nota {
        opacity: 0.6;
    }

    .xrv-grafico {
        width: 100%;
        height: 52px;
        margin-top: 10px;
        display: block;
    }

    /* ---------- ajuda ---------- */
    .xrv-ajuda {
        display: grid;
        grid-template-columns: max-content 1fr;
        gap: 6px 16px;
        margin: 0;
        font-size: 14px;
    }
    .xrv-ajuda dt {
        font-weight: 600;
    }
    .xrv-ajuda dd {
        margin: 0;
        opacity: 0.8;
    }

    /* ---------- celular: dois meses por vez ---------- */
    @media (max-width: 900px) {
        .xrv-grade {
            gap: 10px 10px;
        }
        .xrv-cartao {
            padding: 10px 10px 0px 10px;
        }
        .xrv-valor {
            font-size: 22px;
        }
        .xrv-grafico {
            height: 40px;
        }
        .xrv-ajuda {
            grid-template-columns: 1fr;
            gap: 2px;
        }
        .xrv-ajuda dd {
            margin-bottom: 8px;
        }
    }
</style>
